import { Injectable, inject } from '@angular/core';
import { LoggerService } from '../logger/logger.service';
import { TelemetryEventStoreService } from './telemetry-event-store.service';
import { TelemetrySyncService } from './telemetry-sync.service';
import { VdiClickEventsService } from '../analytics/vdi-click-events.service';
import { environment } from '../../../environments/environment';
import {
  VDI_EVENT_CONSTANTS,
  MAX_EVENT_PAYLOAD_BYTES,
  sanitizePayload,
  normalizeTelemetryTimestamp,
} from './telemetry.types';

const TAG = 'TelemetryVdiBridge';

/**
 * Control and handshake messages used internally by HyLite / VDI wrapper
 * that should not be treated as telemetry events.
 */
const IGNORED_HYLITE_MESSAGES = new Set<string>([
  'extapp-ready',
  'extapp-session-ended',
  'camera-launch-url',
  'extapp-creds',
  'hylite-auth-token',
]);

/**
 * Listens for `postMessage` events from the active HyLite iframe.
 *
 * Routes messages dynamically based on their stream type:
 *  - `stream: 'click_event'` -> Delegated directly to VdiClickEventsService (Analytics / POST api/log/user)
 *  - Telemetry messages -> Validated, sanitized, and enqueued into IndexedDB for batch syncing.
 */
@Injectable({ providedIn: 'root' })
export class TelemetryVdiBridgeService {
  private store = inject(TelemetryEventStoreService);
  private sync = inject(TelemetrySyncService);
  private clickEventsService = inject(VdiClickEventsService);
  private logger = inject(LoggerService);

  private activeSessionId: string | null = null;
  private activeOrigin: string | null = null;
  private activeIframeWindow: Window | null = null;
  private messageHandler: ((e: MessageEvent) => void) | null = null;
  private localSequence = 0;

  /**
   * Begin accepting events for the given VDI session.
   * @param sessionId — the PWA's generated VDI session ID.
   * @param hyLiteOrigin — the resolved HyLite origin (e.g. `https://demo.jiocloudpc.in`).
   * @param iframeWindow — the `contentWindow` of the HyLite iframe element.
   */
  startCapture(sessionId: string, hyLiteOrigin: string, iframeWindow: Window | null): void {
    this.stopCapture(); // idempotent
    this.activeSessionId = sessionId;
    this.activeOrigin = hyLiteOrigin;
    this.activeIframeWindow = iframeWindow;
    this.localSequence = 0;

    this.messageHandler = (event: MessageEvent) => {
      void this.handleMessage(event);
    };
    window.addEventListener('message', this.messageHandler);
    this.logger.info(TAG, 'capture started', { sessionId, hyLiteOrigin });
  }

  /** Stop accepting events and remove the message listener. */
  stopCapture(): void {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }
    this.activeSessionId = null;
    this.activeOrigin = null;
    this.activeIframeWindow = null;
    this.localSequence = 0;
    this.logger.info(TAG, 'capture stopped');
  }

  private async handleMessage(event: MessageEvent): Promise<void> {
    if (!event.data || typeof event.data !== 'object') return;

    // 1. Strict Origin check: must match active VDI origin
    if (!this.activeOrigin || !event.origin || event.origin !== this.activeOrigin) {
      this.logger.warn(TAG, 'rejected: origin mismatch or unverified origin', { received: event.origin, expected: this.activeOrigin });
      return;
    }

    // 2. Source check: must come from the HyLite iframe window (if specified)
    if (this.activeIframeWindow && event.source && event.source !== this.activeIframeWindow) {
      this.logger.warn(TAG, 'rejected: source is not the active HyLite iframe');
      return;
    }

    const data = event.data as Record<string, unknown>;

    // 3. VDI Click Event Discrimination
    if (data['stream'] === 'click_event') {
      this.clickEventsService.processClickEvent(data, this.activeSessionId);
      return;
    }

    // 4. VDI Telemetry Pipeline
    if (environment.telemetry && !environment.telemetry.enabled) return;

    // Identify telemetry event type generically
    let eventType: string | null = null;
    if (typeof data['eventType'] === 'string' && data['eventType'].trim()) {
      eventType = data['eventType'].trim();
    } else if (data['type'] === 'vdi-telemetry' && typeof data['eventType'] === 'string' && data['eventType'].trim()) {
      eventType = data['eventType'].trim();
    } else if (typeof data['type'] === 'string' && data['type'] !== 'vdi-telemetry' && data['type'].trim()) {
      eventType = data['type'].trim();
    }

    // Fast-path: ignore non-telemetry / internal control messages
    if (!eventType || IGNORED_HYLITE_MESSAGES.has(eventType)) return;

    // 4. Session ID check (fallback to activeSessionId if omitted by VDI)
    const rawSessionId = data['vdiSessionId'] ?? data['sessionId'];
    const vdiSessionId = typeof rawSessionId === 'string' && rawSessionId ? rawSessionId : this.activeSessionId;
    if (!vdiSessionId) {
      this.logger.warn(TAG, 'rejected: no active vdiSessionId found');
      return;
    }
    if (this.activeSessionId && vdiSessionId !== this.activeSessionId) {
      this.logger.warn(TAG, 'rejected: vdiSessionId mismatch', {
        received: vdiSessionId,
        expected: this.activeSessionId,
      });
      return;
    }

    // 5. Sequence & Timestamp normalization (handles 10-digit/13-digit/string timestamps)
    const timestamp = normalizeTelemetryTimestamp(data['timestamp']);
    const sequence = typeof data['sequence'] === 'number' ? data['sequence'] : ++this.localSequence;

    // 6. Extract payload directly and sanitize credentials before storing (CWE-522)
    let sanitizedObj: Record<string, unknown>;
    if (typeof data['payload'] === 'string') {
      try {
        const parsed = JSON.parse(data['payload']);
        sanitizedObj = typeof parsed === 'object' && parsed !== null ? sanitizePayload(parsed as Record<string, unknown>) : { message: data['payload'] };
      } catch {
        sanitizedObj = { message: data['payload'] };
      }
    } else if (data['payload'] && typeof data['payload'] === 'object' && !Array.isArray(data['payload'])) {
      sanitizedObj = sanitizePayload(data['payload'] as Record<string, unknown>);
    } else {
      const copy = { ...data };
      delete copy['type'];
      delete copy['eventType'];
      delete copy['timestamp'];
      sanitizedObj = sanitizePayload(copy);
    }

    let payloadStr: string;
    try {
      payloadStr = JSON.stringify(sanitizedObj);
    } catch {
      this.logger.warn(TAG, 'rejected: payload is not JSON-serialisable');
      return;
    }

    // Debug logging for local DEV environment only
    const buildType = environment.buildType as string;
    if (buildType === 'dev' || buildType === 'local') {
      console.log(
        `%c[VDI Telemetry postMessage]%c ${eventType}`,
        'background: #7C3AED; color: #FFFFFF; font-weight: bold; padding: 2px 6px; border-radius: 4px 0 0 4px;',
        'background: #2563EB; color: #FFFFFF; font-weight: bold; padding: 2px 6px; border-radius: 0 4px 4px 0;',
        {
          origin: event.origin,
          eventType,
          sanitizedPayload: sanitizedObj,
        },
      );
    }

    if (new TextEncoder().encode(payloadStr).length > MAX_EVENT_PAYLOAD_BYTES) {
      this.logger.warn(TAG, 'rejected: payload exceeds 64 KiB');
      return;
    }

    // 7. Enqueue into IndexedDB
    const result = await this.store.enqueue({
      vdiSessionId,
      eventType,
      payload: payloadStr,
      timestamp,
      sequence,
    });

    if (result === 'queued') {
      this.logger.debug(TAG, 'event enqueued in local store', { eventType, sequence });
      const syncWhileActive = environment.telemetry?.syncWhileVdiActive ?? true;
      if (!syncWhileActive) {
        // Real-time mode batches only events received close together from the VDI.
        this.sync.scheduleDrain();
      } else if (
        // Periodic mode still flushes immediately when the VDI reports a terminal event.
        eventType === VDI_EVENT_CONSTANTS.RDP_DISCONNECT ||
        eventType === 'vdi_disconnect' ||
        eventType === 'hyworks_logout'
      ) {
        this.logger.info(TAG, `terminal event received (${eventType}) — triggering immediate batch sync`);
        void this.sync.drain();
      }
    } else {
      this.logger.warn(TAG, 'event not queued', { result });
    }
  }
}
